import { ProfileState } from './state';

/**
 * Fields that are inputs to the household recommendation API
 * (`create_or_update_household`). Any `.set()` or `.setIn()` call targeting
 * one of these top-level keys automatically marks `householdDataChanged: true`,
 * which triggers a debounced household re-submission and incentive refresh.
 *
 * Non-user-initiated mutations (API prefill, sanitizeProfileInputs, coverage
 * selection) should use the bypass methods (`.setRaw()`, `.setRawIn()`,
 * `.mergeDeepRaw()`) to avoid triggering re-submission.
 */
export const HOUSEHOLD_FIELDS = new Set([
  'policyholder',
  'spouse',
  'dependents',
  'zipcode',
  'state_code',
  'filing_status',
  'survey',
  'clientSurveyResponses',
  'hsa_funds',
  'networkPreferenceSurveyAnswers',
]);

export class HouseholdTrackingProfileState {
  private state: ProfileState;

  constructor(state: ProfileState) {
    this.state = state;
  }

  // --- Tracking methods: auto-set householdDataChanged for household fields ---

  set(field: any, value: any): HouseholdTrackingProfileState {
    let newState = this.state.set(field, value);
    if (HOUSEHOLD_FIELDS.has(field)) {
      newState = newState.set('householdDataChanged', true);
    }
    return new HouseholdTrackingProfileState(newState);
  }

  setIn(path: any[], value: any): HouseholdTrackingProfileState {
    let newState = this.state.setIn(path, value);
    if (HOUSEHOLD_FIELDS.has(path[0])) {
      newState = newState.set('householdDataChanged', true);
    }
    return new HouseholdTrackingProfileState(newState);
  }

  // --- Bypass methods: no tracking, for non-user-initiated mutations ---

  setRaw(field: any, value: any): HouseholdTrackingProfileState {
    return new HouseholdTrackingProfileState(this.state.set(field, value));
  }

  setRawIn(path: any[], value: any): HouseholdTrackingProfileState {
    return new HouseholdTrackingProfileState(this.state.setIn(path, value));
  }

  mergeDeepRaw(obj: any): HouseholdTrackingProfileState {
    return new HouseholdTrackingProfileState(this.state.mergeDeep(obj));
  }

  // --- Read methods: delegate directly ---

  get(field: any, notSetValue?: any): any {
    return this.state.get(field, notSetValue);
  }

  getIn(path: any[], notSetValue?: any): any {
    return this.state.getIn(path, notSetValue);
  }

  toJS(): any {
    return this.state.toJS();
  }

  equals(other: any): boolean {
    const otherState = other instanceof HouseholdTrackingProfileState ? other.state : other;
    return this.state.equals(otherState);
  }

  // --- Unwrap to get the underlying Immutable state ---

  unwrap(): ProfileState {
    return this.state;
  }
}
